import { ToastContext } from "context/ToastContext";
import { centsToDollars } from "helpers/price";
import { useOrder } from "hooks/useOrder";
import { uniqBy } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useState, useContext, useMemo } from "react";
import { useMarkPaidViaCashUnmanagedMutation } from "store/queries/order";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";
import { SpecialtyList } from "types/Provider/Provider";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 1rem;
  color: #856404;
`;

const PaymentBreakdownBox = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
`;

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
`;

const ConfirmationBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

interface IMarkAsPaidCashUnmanagedDialogProps {
  order: IOrder;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  onUpdateOrder: () => void;
}

export function MarkAsPaidCashUnmanagedDialog({
  order,
  showDialog,
  setShowDialog,
  onUpdateOrder,
}: IMarkAsPaidCashUnmanagedDialogProps) {
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  const { isPatientResponsibility } = useOrder(order);

  const [markPaidViaCashUnmanaged, { isLoading }] =
    useMarkPaidViaCashUnmanagedMutation();

  // Check if this is a multi-provider order
  const providers = useMemo(() => {
    const mandatoryFacility = order.account.providers
      .filter((prov) => prov.specialty === SpecialtyList.FACILITY)
      .map((prov) => ({
        providerId: prov.id,
        providerName: prov.name,
        specialty: prov.specialty,
      }));

    const procedureProviders = order.serviceDetails.procedures.map(
      (provider: any) => ({
        providerId: provider.providerId,
        providerName: provider.providerName,
        specialty: provider.specialty,
      })
    );

    return uniqBy([...procedureProviders, ...mandatoryFacility], "providerId");
  }, [order]);

  const isMultiProvider = providers.length > 1;

  // Calculate payment breakdown by provider
  const paymentBreakdown = useMemo(() => {
    if (isPatientResponsibility) {
      return null;
    }

    const amountsByProvider: Record<
      string,
      { providerName: string; amount: number }
    > = {};

    const bundledAmounts =
      order.paymentFullBreakdown?.bundledProviderAmounts || [];
    bundledAmounts.forEach((providerAmount) => {
      const id = providerAmount.providerId || "unknown";
      if (!amountsByProvider[id]) {
        amountsByProvider[id] = {
          providerName: providerAmount.providerName || "Unknown Provider",
          amount: 0,
        };
      }
      amountsByProvider[id].amount += providerAmount.clearDiscountedPrice || 0;
    });

    return Object.values(amountsByProvider);
  }, [order, isPatientResponsibility]);

  const handleClose = () => {
    setShowDialog(false);
    setNotes("");
    setConfirmed(false);
  };

  const handleSubmit = async () => {
    if (!notes.trim()) {
      toastElement?.show({
        severity: "error",
        summary: "Note Required",
        detail: "Please provide a note for this payment.",
      });
      return;
    }

    if (!confirmed) {
      toastElement?.show({
        severity: "error",
        summary: "Confirmation Required",
        detail: "Please confirm you understand the payment terms.",
      });
      return;
    }

    try {
      await markPaidViaCashUnmanaged({
        orderId: order.id,
        note: notes.trim(),
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success",
        detail: "Estimate has been marked as paid via cash.",
      });

      handleClose();
      onUpdateOrder();
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to mark estimate as paid. Please try again.",
      });
    }
  };

  const footerComponent = () => {
    return (
      <div>
        <Button
          label="Cancel"
          onClick={handleClose}
          className="mr-2 p-button-secondary"
          disabled={isLoading}
        />
        <Button
          label={isLoading ? "Processing..." : "Mark as Paid"}
          onClick={handleSubmit}
          disabled={isLoading || !notes.trim() || !confirmed}
        />
      </div>
    );
  };

  return (
    <Dialog
      onHide={handleClose}
      visible={showDialog}
      header="Mark as Paid via Cash"
      footer={footerComponent}
      style={{ width: "500px" }}
    >
      <Container>
        <WarningBox>
          <strong>Important:</strong>
          <ul style={{ marginBottom: 0, paddingLeft: "1.25rem" }}>
            <li>
              Cash payments marked here will <strong>NOT</strong> be recouped or
              redistributed by Clear Health.
            </li>
            <li>
              This payment will <strong>NOT</strong> appear on your weekly
              payment reports.
            </li>
          </ul>
        </WarningBox>

        {isMultiProvider && paymentBreakdown && paymentBreakdown.length > 0 && (
          <PaymentBreakdownBox>
            <strong>Payment Breakdown:</strong>
            {paymentBreakdown.map((item, idx) => (
              <PaymentItem key={idx}>
                <span>{item.providerName}</span>
                <span>${centsToDollars(item.amount).toFixed(2)}</span>
              </PaymentItem>
            ))}
            <hr style={{ margin: "0.5rem 0" }} />
            <PaymentItem>
              <strong>Total</strong>
              <strong>
                $
                {centsToDollars(
                  paymentBreakdown.reduce((sum, item) => sum + item.amount, 0)
                ).toFixed(2)}
              </strong>
            </PaymentItem>
          </PaymentBreakdownBox>
        )}

        <div>
          <label htmlFor="notes" style={{ fontWeight: "bold" }}>
            Notes (Required):
          </label>
          <InputTextarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Enter payment details, receipt number, or other relevant information..."
            className="w-full mt-2"
            disabled={isLoading}
          />
        </div>

        <ConfirmationBox>
          <Checkbox
            inputId="confirm"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.checked || false)}
            disabled={isLoading}
          />
          <label htmlFor="confirm" style={{ cursor: "pointer" }}>
            {isMultiProvider
              ? "I confirm that payments were already collected and will be distributed internally."
              : "I confirm that the payment has been collected and I understand Clear Health will not process this payment."}
          </label>
        </ConfirmationBox>
      </Container>
    </Dialog>
  );
}
